import type {AttributeDefinition, ValidationRules} from '@/types/attribute-definition'
import {ValueSourceType} from '@/types/attribute-definition'
import {AttributeValueType} from '@/types/attribute'
import type {useI18n} from '@/i18n'

type I18n = Pick<ReturnType<typeof useI18n>, 'attributeValidation'>

/**
 * Client-side validation mirroring backend AttributeAuthorityService.validateValue().
 * Returns an error message or null if valid.
 */
export function validateValue(definition: AttributeDefinition, value: string, i18n: I18n): string | null {
    const rules = definition.validationRules
    if (!rules) return null
    if (!value) return null

    const name = definition.displayName || definition.name

    if (definition.valueType === AttributeValueType.STRING || definition.valueType === AttributeValueType.REFERENCE) {
        return validateStringRules(name, value, rules, i18n)
    }

    if (definition.valueType === AttributeValueType.NUMBER) {
        return validateNumberRules(name, value, rules, i18n)
    }

    return null
}

function validateStringRules(name: string, value: string, rules: ValidationRules, i18n: I18n): string | null {
    const trimmed = value.trim()
    if (rules.minLength != null && trimmed.length < rules.minLength) {
        return i18n.attributeValidation.minLength({name, min: rules.minLength})
    }
    if (rules.maxLength != null && value.length > rules.maxLength) {
        return i18n.attributeValidation.maxLength({name, max: rules.maxLength})
    }
    if (rules.pattern != null) {
        try {
            const regex = new RegExp(rules.pattern)
            if (!regex.test(value)) {
                return i18n.attributeValidation.pattern({name})
            }
        // eslint-disable-next-line no-restricted-syntax -- invalid regex from user input, error surfaced as validation message
        } catch {
            return i18n.attributeValidation.invalidPattern({name})
        }
    }
    if (rules.valueSource?.type === ValueSourceType.LIST && rules.valueSource.values != null) {
        const allowedCodes = rules.valueSource.values.map(e => e.code)
        if (!allowedCodes.includes(value)) {
            return i18n.attributeValidation.allowedValues({name, values: allowedCodes.join(', ')})
        }
    }
    return null
}

function validateNumberRules(name: string, value: string, rules: ValidationRules, i18n: I18n): string | null {
    if (value.trim().length === 0) {
        return i18n.attributeValidation.invalidNumber({name})
    }
    const num = Number(value)
    if (isNaN(num)) {
        return i18n.attributeValidation.invalidNumber({name})
    }

    if (rules.min != null && num < rules.min) {
        return i18n.attributeValidation.minValue({name, min: rules.min})
    }
    if (rules.max != null && num > rules.max) {
        return i18n.attributeValidation.maxValue({name, max: rules.max})
    }
    return null
}

/**
 * Computes hint text from validation rules for display below the input.
 */
export function getHintText(definition: AttributeDefinition, i18n: I18n): string | null {
    const rules = definition.validationRules
    if (!rules) return null

    if (definition.valueType === AttributeValueType.STRING || definition.valueType === AttributeValueType.REFERENCE) {
        const parts: string[] = []
        if (rules.minLength != null && rules.maxLength != null) {
            if (rules.minLength === rules.maxLength) {
                parts.push(i18n.attributeValidation.hintCharExact({n: rules.minLength}))
            } else {
                parts.push(i18n.attributeValidation.hintCharRange({min: rules.minLength, max: rules.maxLength}))
            }
        } else if (rules.minLength != null) {
            parts.push(i18n.attributeValidation.hintCharMin({min: rules.minLength}))
        } else if (rules.maxLength != null) {
            parts.push(i18n.attributeValidation.hintCharMax({max: rules.maxLength}))
        }
        if (rules.pattern != null) {
            parts.push(i18n.attributeValidation.hintPattern({pattern: rules.pattern}))
        }
        return parts.length > 0 ? parts.join(' \u00b7 ') : null
    }

    if (definition.valueType === AttributeValueType.NUMBER) {
        if (rules.min != null && rules.max != null) {
            return i18n.attributeValidation.hintRange({min: rules.min, max: rules.max})
        }
        if (rules.min != null) {
            return i18n.attributeValidation.hintMin({min: rules.min})
        }
        if (rules.max != null) {
            return i18n.attributeValidation.hintMax({max: rules.max})
        }
    }

    return null
}

/**
 * Returns HTML input constraints derived from validation rules.
 */
export function getInputConstraints(definition: AttributeDefinition): {
    min?: number
    max?: number
    maxLength?: number
} {
    const rules = definition.validationRules
    if (!rules) return {}

    const constraints: {min?: number; max?: number; maxLength?: number} = {}

    if (definition.valueType === AttributeValueType.STRING || definition.valueType === AttributeValueType.REFERENCE) {
        if (rules.maxLength != null) constraints.maxLength = rules.maxLength
    }

    if (definition.valueType === AttributeValueType.NUMBER) {
        if (rules.min != null) constraints.min = rules.min
        if (rules.max != null) constraints.max = rules.max
    }

    return constraints
}
